import { request } from "./apiClient";
import type { AuthRawResponse, UserProfile } from "../types/auth.types";

// apiClient's `request` is untyped JS; give it a proper signature here. `body`
// and `token` are optional at runtime (the inferred type wrongly requires them).
const apiRequest = request as unknown as (
  path: string,
  options?: { method?: string; body?: unknown; auth?: boolean; token?: string; silent?: boolean }
) => Promise<unknown>;

// Log a user in. Returns { jwt, id, user } on success.
export const login = async (email: string, password: string): Promise<AuthRawResponse> => {
  try {
    return (await apiRequest("/login", {
      method: "POST",
      body: { email, password },
    })) as AuthRawResponse;
  } catch (error) {
    const err = error as { status?: number; message?: string };
    // 401 on login means bad credentials, not an expired session.
    if (err.status === 401) {
      throw new Error("Incorrect email or password.");
    }
    throw new Error("We're having trouble connecting. Please try again.");
  }
};

// Register a new user. Payload is built by the sign-up form as
// { user: Record<string, string>, role }.
export const signUp = async (
  userData: { user: Record<string, string>; role: string }
): Promise<AuthRawResponse> => {
  return (await apiRequest("/register", {
    method: "POST",
    body: userData,
  })) as AuthRawResponse;
};

// Fetch the signed-in user's profile. Pass an explicit token because during
// login the fresh JWT is not yet in the store. `silent` skips the global
// 401 handler so background validation can fail without side effects.
export const fetchUserProfile = async (
  userId: number | string,
  token: string,
  options?: { silent?: boolean }
): Promise<UserProfile> => {
  const data = (await apiRequest(`/users/${userId}`, {
    auth: true,
    token,
    ...options,
  })) as { user: UserProfile };
  return data.user;
};

// Request a password reset email. Always returns a generic success message to
// avoid revealing whether an account with that email exists.
export const requestPasswordReset = async (email: string): Promise<unknown> => {
  return apiRequest("/password_resets", { method: "POST", body: { email } });
};

// Set a new password using a reset token (backend expects PUT).
export const resetPassword = async (
  token: string,
  password: string,
  passwordConfirmation: string
): Promise<unknown> => {
  return apiRequest("/password_resets", {
    method: "PUT",
    body: { token, password, password_confirmation: passwordConfirmation },
  });
};

// Add an additional role to the current user (e.g. tenant -> landlord).
// Backend: POST /users/roles { role: "landlord"|"tenant"|"admin" }.
export const addRole = async (role: string): Promise<unknown> => {
  return apiRequest("/users/roles", { method: "POST", body: { role }, auth: true });
};

// Confirm an email address from the link sent after signup.
// Backend: POST /users/confirm { token }.
export const confirmEmail = async (token: string): Promise<unknown> => {
  return apiRequest("/users/confirm", { method: "POST", body: { token } });
};
